import React, {useCallback, useContext, useEffect} from 'react';

import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {FlatButton} from '@pb/kit-component-button';
import {Icon, Icons} from '@pb/kit-component-icon';
import {SlideBoxButton} from '@pb/kit-component-slide-box';

import {CrmRow} from '../../components/crm-row';
import {useEnabledCrmModules} from '../../useEnabledCrmModules';

import {PitchBookSection} from './pitchbook-section';
import {usePitchBookCrmSettings} from './usePitchBookCrmSettings';

import type {CrmComponentProps} from '../../license-crm-tab';
import type {MessageContextType} from 'src/contexts/message-context';


export const PitchBookRow = (props: CrmComponentProps) => {
    const {
        accountId,
        licenseId,
        currentCrm,
        runSaveChanges,
        runDiscardChanges,

        onChangeActiveCrm,
        onChangesSaved,
        onChangesDiscarded,
        onActionDisabled,
        editDisabled,
    } = props;

    const {setErrorMessage, setSuccessMessage} = useContext<MessageContextType>(MessageContext);

    const {isSomeChunkChanged, isCurrentChunkChanged, setIsChunkChanged, onShowModal, proceed} = useConfirmationChunk({
        chunkName: 'pitchbook-crm',
        onProceed: () => {
            onChangesSaved();
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isModuleLoading,
        loadingError: moduleLoadingError,
        enabled,
        saveCrmModuleState,
    } = useEnabledCrmModules({licenseId, crmType: CrmTypes.PITCHBOOK});

    const {
        isLoading: isSettingsLoading,
        loadingError: settingsLoadingError,
        isChanged: isSettingsChanged,

        data: settings,
        setData: setSettings,

        saveChanges: saveSettings,
        discardChanges: discardSettings,
        loadData: loadSettings,
    } = usePitchBookCrmSettings({accountId});

    const saveChanges = useCallback(async () => {
        const calls = [];

        if (isSettingsChanged) {
            calls.push(await saveSettings());
        }

        const errors = calls.filter(Boolean);

        if (errors.length) {
            onChangesSaved();
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isSettingsChanged,
        saveSettings,
        onChangesSaved,
        setErrorMessage,
        proceed,
    ]);

    const discardChanges = useCallback(() => {
        discardSettings();

        onChangesDiscarded();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardSettings, onChangesDiscarded, setSuccessMessage]);

    const changeActiveCrm = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(currentCrm !== CrmTypes.PITCHBOOK ? CrmTypes.PITCHBOOK : null);
            });
        } else {
            onChangeActiveCrm(currentCrm !== CrmTypes.PITCHBOOK ? CrmTypes.PITCHBOOK : null);
        }
    }, [isCurrentChunkChanged, discardChanges, currentCrm, onChangeActiveCrm, onShowModal]);

    const toggleCrmModules = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
        if (enabled && isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(null);
                saveCrmModuleState(false);
            });
        } else if (enabled) {
            onChangeActiveCrm(null);
            saveCrmModuleState(false);
        } else {
            saveCrmModuleState(true);
        }
        event.preventDefault();
    }, [
        isCurrentChunkChanged,
        discardChanges,
        enabled,
        saveCrmModuleState,
        onChangeActiveCrm,
        onShowModal,
    ]);

    useEffectWithDeps((prev, current) => {
        if (currentCrm === CrmTypes.PITCHBOOK && prev.currentCrm !== current.currentCrm && loadSettings) {
            loadSettings();
        }
    }, {currentCrm, loadSettings});

    useEffect(() => {
        setIsChunkChanged([isSettingsChanged].some(Boolean));
    }, [isSettingsChanged, setIsChunkChanged]);

    useEffect(() => {
        if (runSaveChanges && isCurrentChunkChanged) {
            saveChanges();
        }
    }, [runSaveChanges, saveChanges, isCurrentChunkChanged]);

    useEffect(() => {
        if (runDiscardChanges && isCurrentChunkChanged) {
            discardChanges();
        }
    }, [runDiscardChanges, discardChanges, isCurrentChunkChanged]);

    const loadingError = [
        moduleLoadingError,
        settingsLoadingError,
    ].find(Boolean) || null;

    useEffect(() => {
        onActionDisabled(!isCurrentChunkChanged || isModuleLoading || isSettingsLoading || !!loadingError);
    }, [isCurrentChunkChanged, isModuleLoading, isSettingsLoading, loadingError, onActionDisabled]);

    return (
        <CrmRow
            isLoading={isModuleLoading}
            currentCrm={currentCrm}
            loadingError={loadingError}
            editDisabled={editDisabled}
            crmType={CrmTypes.PITCHBOOK}
            crmName="PitchBook"
            enabled={enabled}
            onToggleCrmModules={toggleCrmModules}
            connectionStatus={<div/>}
            slideBoxButton={(
                <SlideBoxButton onClick={changeActiveCrm}>
                    {() => (
                        <div className="pdy-xs">
                            <FlatButton
                                startIcon={<Icon type={Icons.PENCIL}/>}
                                onClick={changeActiveCrm}
                                disabled={
                                    isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                    currentCrm !== CrmTypes.PITCHBOOK
                                }
                                endIcon={
                                    <Icon type={currentCrm === CrmTypes.PITCHBOOK ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}/>
                                }>
                                Configure settings
                            </FlatButton>
                        </div>
                    )}
                </SlideBoxButton>
            )}>

            <PitchBookSection
                isLoading={isSettingsLoading}
                disabled={editDisabled || !enabled}
                settings={settings}
                setSettings={setSettings}
            />
        </CrmRow>
    );
};
